// src/components/Assets/hooks/useAssets.js
// Custom hook for Assets CRUD operations - Phase 2 implementation

import { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast";
import * as assetsApi from "../assetsApi";

/**
 * Custom hook for managing assets
 * Provides CRUD operations and state management for assets
 */
export const useAssets = () => {
  const { token, userData } = useContext(UserContext);
  const [t] = useTranslation();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extract tenantId from nested userData structure
  const tenantId = userData?.Tenant?.id || userData?.User?.tenantId || userData?.Employee?.tenantId;

  /**
   * Fetch all assets for the current tenant
   */
  const fetchAssets = useCallback(async (abortSignal) => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await assetsApi.fetchTenantAssets(tenantId, token, abortSignal);
      setAssets(data);
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        toast.error(t("Failed to load assets"));
        console.error("Error fetching assets:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, token]);

  /**
   * Add a new asset
   */
  const addAsset = async (asset) => {
    if (!tenantId) {
      toast.error(t("Tenant ID is missing. Please log in again."));
      console.error("tenantId is missing from userData");
      return false;
    }

    try {
      const response = await assetsApi.createAsset({ ...asset, tenantId }, token);

      // Check if backend returned an error in the response body (HTTP 200 but body.status >= 400)
      if (response.status && response.status >= 400) {
        toast.error(t(response.message) || t("Failed to add asset"));
        console.error("Error in response body:", response);
        return false;
      }

      toast.success(t(response.message) || t("Asset added successfully"));
      await fetchAssets();
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || t("Failed to add asset");
      toast.error(t(errorMessage));
      console.error("Error adding asset:", err);
      return false;
    }
  };

  /**
   * Update an existing asset
   */
  const editAsset = async (asset) => {
    try {
      const response = await assetsApi.updateAsset({ ...asset, tenantId }, token);

      // Check if backend returned an error in the response body (HTTP 200 but body.status >= 400)
      if (response.status && response.status >= 400) {
        toast.error(t(response.message) || t("Failed to update asset"));
        console.error("Error in response body:", response);
        return false;
      }

      toast.success(t(response.message) || t("Asset updated successfully"));
      await fetchAssets();
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || t("Failed to update asset");
      toast.error(t(errorMessage));
      console.error("Error updating asset:", err);
      return false;
    }
  };

  /**
   * Delete an asset
   */
  const removeAsset = async (assetId) => {
    try {
      const response = await assetsApi.deleteAsset(assetId, token);

      // Check if backend returned an error in the response body (HTTP 200 but body.status >= 400)
      if (response.status && response.status >= 400) {
        toast.error(t(response.message) || t("Failed to delete asset"));
        console.error("Error in response body:", response);
        return false;
      }

      toast.success(t(response.message) || t("Asset deleted successfully"));
      await fetchAssets();
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || t("Failed to delete asset");
      toast.error(t(errorMessage));
      console.error("Error deleting asset:", err);
      return false;
    }
  };

  // Fetch assets on mount and when dependencies change
  useEffect(() => {
    const abortController = new AbortController();
    fetchAssets(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchAssets]);

  return {
    assets,
    loading,
    error,
    fetchAssets,
    addAsset,
    editAsset,
    removeAsset
  };
};

export default useAssets;
